import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchLabelsByRequestType } from '../api/Request.client';
import { queryKeys } from '../api/queryKeys';
import { ILabel } from '../constants/interfaces';
import { getFieldDefaultValue } from '../utils/formUtils';
import { FormState, FormFieldValue } from './useFormState';

export function useFormLabels(
  requestTypeId: number,
  setLoading: (val: boolean) => void
) {
  // Local labels state — seeded from React Query, overridable via setLabels for compat
  const [labels, setLabels] = useState<ILabel[]>([]);
  const [formState, setFormState] = useState<FormState>({});

  const { data: fetchedLabels = [], isLoading } = useQuery({
    enabled: requestTypeId > 0,
    gcTime: 8 * 60 * 60 * 1000,
    queryFn: () => fetchLabelsByRequestType(requestTypeId),
    queryKey: queryKeys.requestLabels(requestTypeId),
    staleTime: Infinity, // 8 hours
  });

  // Sync query loading state with parent
  useEffect(() => {
    setLoading(isLoading);
  }, [isLoading, setLoading]);

  // Seed labels + initial form state when query data arrives
  useEffect(() => {
    const typed = fetchedLabels as ILabel[];
    if (typed.length > 0) {
      setLabels(typed);
      const initialState: Record<string, FormFieldValue> = {};
      typed.forEach((field) => {
        const defaultValue = getFieldDefaultValue(field);
        initialState[field.labelId] = { value: defaultValue };
      });
      setFormState(initialState);
    } else if (!isLoading) {
      setLabels([]);
      setFormState({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchedLabels]);

  return { formState, labels, setFormState, setLabels };
}
